/*
 * A typescript & react boilerplate with api call example
 *
 * Copyright (C) 2020 Adam van der Kruk aka TacB0sS
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { LoggedStatus, ModuleFE_Account } from '@nu-art/user-account-frontend/index';
import { ModuleFE_Dialog, ModuleFE_RoutingV2, ModuleFE_ServerInfo, ModuleFE_SyncManager, ModuleFE_Thunderstorm, StorageKey, Thunder, ThunderDispatcher } from '@nu-art/thunderstorm-frontend/index';
import { arrayToMap, BadImplementationException, Module } from '@nu-art/ts-common';
import { Component_HealthcareSpace, Page_ContextRulesManager, Page_ResourceEditor_V2 } from '@app/hcs-frontend';
import { Page_DiseaseProfile } from '@app/dp-frontend';
import { ModuleFE_EHRInstallation, ModuleFE_Environment, ModuleFE_Facility, ModuleFE_Organization, Page_OrganizationEditor } from '@app/org-frontend';
import { Page_PathwayEditor, Route_ActionManager } from '@app/pathway-frontend';
import { Page_ExpressionManager } from '@app/hcs-frontend/expression';
import { Route_AdvisorContextEditor } from '@app/advisor-content-frontend/ui/advisor-context-editor/consts';
import { PermissionKey_FE } from '@nu-art/permissions-frontend/PermissionKey_FE';
import { ArtifactsPipelinePermissionKeysNames } from '@app/shared/permissions/permission-manager-ui-permissions';
import { Route_FHIRManager } from '@app/fhir-frontend';
import { Route_AdvisorUIEditor } from '@app/advisor-ui-frontend/consts';
import { Page_AvatarMode } from '@app/advisor-ui-frontend';
import { Route_Page_SmartDocs } from '@app/advisor-content-frontend/ui/pages/Page_SmartDocs/route';
import { ModuleFE_ConflictResolution } from '@nu-art/conflict-resolution-frontend/_modules/ModuleFE_ConflictResolution';
import { Panel_ResourceManager_V1_old } from '../../knowledge-manager/managers/ResourceEditor/index.js';
import { Manager_Permissions } from '../../knowledge-manager/managers/Manager_Permissions/Manager_Permissions.js';
import { Route_Workspace, RouteKey_Workspace } from '../../pages/Page_Workspace/consts.js';
import { ModuleFE_WorkHub } from '@nu-art/work-hub-frontend/modules';
import { Page_Demo } from '../../knowledge-manager/managers/advisor-launch/index.js';
import { Page_EHREmulator } from '@app/ehr-emulator-frontend';
import { PermissionKeys_AdvisorLaunch } from '@app/shared';
import { RoutePermissions } from '@nu-art/permissions-frontend';
import { Page_UnresolvedCodes } from '@app/runtime-tools-frontend';
import { Page_Migration } from '@app/data-migration-frontend';
export const dispatch_onToggleSideNavBarState = new ThunderDispatcher('__onToggleSideNavBarState');
export class ModuleFE_AppModule_Class extends Module {
    permissions;
    advisorLaunch;
    async __onLoginStatusUpdated() {
        const status = ModuleFE_Account.getLoggedStatus();
        switch (status) {
            case LoggedStatus.SESSION_TIMEOUT:
            case LoggedStatus.LOGGED_OUT:
                ModuleFE_ServerInfo.stopListening();
                ModuleFE_SyncManager.stopListening();
                ModuleFE_SyncManager.cancelSync();
                ModuleFE_Dialog.close();
                await ModuleFE_Thunderstorm.clearWebsiteData();
                break;
            case LoggedStatus.VALIDATING:
                break;
            case LoggedStatus.LOGGED_IN:
                ModuleFE_SyncManager.startListening();
                ModuleFE_ServerInfo.startListening();
                break;
            default:
                throw new BadImplementationException(`logged status can only be one of 'LOGGED_IN', 'LOGGED_OUT' or 'VALIDATING'`);
        }
    }
    constructor() {
        super();
        this.permissions = {
            ui: PermissionKey_FE.generatePermissionKeysByLevels(ArtifactsPipelinePermissionKeysNames),
            collection: {}
        };
        this.advisorLaunch = PermissionKey_FE.generatePermissionKeysByLevels(PermissionKeys_AdvisorLaunch);
    }
    //NavBar Related Things
    getNavBarStatus() {
        return this.navBarStatus.get(false);
    }
    navBarStatus = new StorageKey('nav-bar__is-open');
    init() {
        this.navBarStatus.set(false);
        ModuleFE_ConflictResolution.initDefaultHasDependencyResponse();
        ModuleFE_WorkHub.setPostTabAdditionCallback(() => {
            if (ModuleFE_RoutingV2.getCurrentRouteKey().key === RouteKey_Workspace)
                return;
            const route = ModuleFE_RoutingV2.getRouteByKey(RouteKey_Workspace);
            if (!route)
                return;
            ModuleFE_RoutingV2.goToRoute(route);
        });
    }
    toggleSideNavBar() {
        const lastNavBarStatus = this.navBarStatus.get();
        this.navBarStatus.set(!lastNavBarStatus);
        dispatch_onToggleSideNavBarState.dispatchUI();
    }
    getEnv() {
        return Thunder.getInstance().getConfig().label; // LOCAL/DEV/STAGING/ empty string for prod
    }
}
export const ModuleFE_AppModule = new ModuleFE_AppModule_Class();
const RouteDomainsArray = [
    {
        route: Route_Workspace,
        icon: 'variable',
        routeTitle: 'Workspace',
    },
    {
        icon: 'healthcareSpace',
        route: Component_HealthcareSpace.Route,
        routeTitle: 'Healthcare Space',
    },
    {
        route: Page_DiseaseProfile.Route,
        icon: 'diseaseProfile',
        routeTitle: 'Disease Profile',
    },
    {
        route: Page_PathwayEditor.Route,
        icon: 'pathways',
        routeTitle: 'Pathway Manager',
    },
    {
        route: Page_ContextRulesManager.Route,
        icon: 'list',
        routeTitle: 'Context Rules Manager',
    },
    {
        route: Route_Page_SmartDocs,
        icon: 'fillForm',
        routeTitle: 'Smart Docs'
    },
    {
        route: Page_ExpressionManager.Route,
        icon: 'expressions',
        routeTitle: 'Expression Manager',
    },
    {
        route: Panel_ResourceManager_V1_old.Route,
        icon: 'resources',
        routeTitle: 'Resource Manager',
        designator: 'RES',
    },
    {
        route: Page_ResourceEditor_V2.Route,
        icon: 'resources',
        routeTitle: 'Resource Manager V2',
        designator: 'v2',
    },
    {
        route: Page_OrganizationEditor.Route,
        icon: 'organization',
        routeTitle: 'Organization Manager V2',
        designator: 'v2',
    },
    {
        route: Route_ActionManager,
        icon: 'resources',
        routeTitle: 'Order Manager',
    },
    {
        route: Manager_Permissions.Route,
        icon: 'userPermissions',
        routeTitle: 'Permissions Manager',
    },
    {
        route: Route_AdvisorContextEditor,
        routeTitle: 'Advisor Context Manager V2',
        icon: 'newAdvisorForm',
        designator: 'V2',
    },
    {
        route: Route_AdvisorUIEditor,
        routeTitle: 'Advisor UI Manager',
        icon: 'advisor_v5_relatedFindings',
    },
    {
        route: Route_FHIRManager,
        routeTitle: 'FHIR Manager',
        icon: 'advisor_avn_gear'
    },
    {
        route: Page_AvatarMode.Route,
        routeTitle: 'Avatar Mode',
        icon: 'users',
    },
    {
        route: {
            key: 'advisor-launch',
            path: 'advisor-launch',
            Component: Page_Demo,
            modulesToAwait: [ModuleFE_Facility, ModuleFE_Environment, ModuleFE_Organization, ModuleFE_EHRInstallation],
            enabled: RoutePermissions(ModuleFE_AppModule.advisorLaunch.Release),
        },
        routeTitle: 'Advisor Launch',
        icon: 'advisorPatient',
    },
    {
        route: Page_EHREmulator.Route,
        routeTitle: 'EHR Emulator',
        icon: 'sync',
    },
    {
        route: Page_UnresolvedCodes.Route,
        routeTitle: 'Unresolved Code Manager',
        icon: 'list',
    },
    {
        route: Page_Migration.Route,
        routeTitle: 'HV Migration',
        icon: 'sync',
    },
];
export const RouteDomainsMap = arrayToMap(RouteDomainsArray, item => item.route.key);
